import React from 'react';
import {
  Card,
  CardHeader,
  FlexBox,
  List,
  ListItemStandard,
  Text,
  Label,
  Link,
  Tag,
  Icon,
  Avatar,
  MessageStrip,
} from '@ui5/webcomponents-react';
import { IllustratedMessage } from '@ui5/webcomponents-react';
import '@ui5/webcomponents-fiori/dist/illustrations/SearchEarth.js';

import "@ui5/webcomponents-icons/dist/AllIcons.js";
import WeatherCard from './WeatherCard';
import LocationMap from './LocationMap';
import GuestRatingCard from './GuestRatingCard';

const CATEGORY_MAP = {
  1: 'Grocery & Shopping',
  2: 'Restaurants & Cafes',
  3: 'Family Entertainment',
  4: 'Adventure & Outdoors',
  5: 'Nightlife & Bars',
  6: 'Health & Wellness',
  7: 'Services & Essentials',
};

function AppShell({ locationData, showDeviceControl, onNavigateToControls }) {
  if (!locationData || locationData.response?.error || locationData.error) {
    return (
      <FlexBox justifyContent="Center" alignItems="Center" style={{ minHeight: '60vh' }}>
        <IllustratedMessage
          name="SearchEarth"
          titleText="Whoops"
          subtitleText="Location not found"
        />
      </FlexBox>
    );
  }

  const mainArr = locationData.main;
  const main = Array.isArray(mainArr) ? mainArr[0] : mainArr;
  const { accessCodes, localTips, location } = locationData;

  const isLongTerm = main.rental_type === 'long';
  const showCheckin = parseInt(main.show_checkin_checkout) === 1;
  const showAccessCodes = parseInt(main.show_access_codes) === 1;
  const showLocalTips = parseInt(main.show_local_tips) === 1;
  const showHouseRules = parseInt(main.show_house_rules) === 1;
  const showMap = parseInt(main.show_location_map) === 1;
  const showWeather = parseInt(main.show_weather) === 1;
  const showGuestRating = locationData.guestContactEnabled === true;


  const checkinLabel = isLongTerm ? 'Move-in' : 'Check-in';
  const checkoutLabel = isLongTerm ? 'Move-out' : 'Check-out';

  return (
    <div className="shell-container">
      {/* Hero Welcome Card */}
      <Card className="hero-card">
        <CardHeader
          avatar={<Avatar icon="home" colorScheme="Accent6" size="S" />}
          titleText={main.welcome_header || main.welcome || 'Welcome'}
          subtitleText={main.welcome_header && main.welcome ? '' : "We're glad you're here"}
          interactive={false}
        />
        <div className="card-body hero-body">
          <FlexBox direction="Column" className="hero-details">
            {main.welcome_header && main.welcome && (
              <Text className="info-value">{main.welcome}</Text>
            )}
            {main.prop_mgt_company && (
              <FlexBox alignItems="Center" className="info-row">
                <Icon name="building" className="info-icon" />
                <div>
                  <Label className="info-label">Property Manager</Label>
                  <Text className="info-value">{main.prop_mgt_company}</Text>
                </div>
              </FlexBox>
            )}
            {main.prop_mgt_phone && (
              <FlexBox alignItems="Center" className="info-row">
                <Icon name="phone" className="info-icon" />
                <div>
                  <Label className="info-label">Phone</Label>
                  <Link href={`tel:${main.prop_mgt_phone}`} className="info-value">
                    {main.prop_mgt_phone}
                  </Link>
                </div>
              </FlexBox>
            )}
            {main.instagram && main.instagram !== '' && (
              <FlexBox alignItems="Center" className="info-row">
                <img src="/img/Instagram.png" alt="Instagram" className="info-icon" style={{ width: '1rem', height: '1rem', objectFit: 'contain' }} />
                <Link href={main.instagram} target="_blank">
                  Instagram
                </Link>
              </FlexBox>
            )}
          </FlexBox>
        </div>
      </Card>

      <div className="card-grid">

        {/* Wifi Card */}
        <Card className="grid-card">
          <CardHeader
            avatar={<Avatar icon="connected" colorScheme="Accent1" />}
            titleText="WiFi"
            subtitleText="Connect to the internet"
          />
          <div className="card-body">
            {main.wifi_ssid && (
              <FlexBox direction="Column" className="wifi-info">
                <div className="wifi-field">
                  <Label className="info-label">Network</Label>
                  <Tag design="Set2" colorScheme="6" className="wifi-tag">
                    {main.wifi_ssid}
                  </Tag>
                </div>
                <div className="wifi-field">
                  <Label className="info-label">Password</Label>
                  <Text className="wifi-password">{main.wifi_pass}</Text>
                </div>
              </FlexBox>
            )}
            {!main.wifi_ssid && (
              <MessageStrip design="Information" hideCloseButton>
                No WiFi information available
              </MessageStrip>
            )}
          </div>
        </Card>

        {/* Weather Card */}
        {showWeather && location && location.zipCode && (
          <WeatherCard zipCode={location.zipCode} country={location.country} />
        )}

        {/* Smart Home Controls Banner */}
        {showDeviceControl && (
          <div className="controls-banner grid-card-full" onClick={onNavigateToControls} role="button" tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && onNavigateToControls()}>
            <FlexBox alignItems="Center" justifyContent="SpaceBetween">
              <FlexBox alignItems="Center" gap="0.75rem">
                <Icon name="home-share" className="controls-banner-icon" />
                <div>
                  <Text className="controls-banner-title">Smart Home Controls</Text>
                  <Label className="controls-banner-subtitle">Lights, locks, thermostat &amp; more</Label>
                </div>
              </FlexBox>
              <Icon name="navigation-right-arrow" className="controls-banner-chevron" />
            </FlexBox>
          </div>
        )}

        {/* Check-in / Move-in Card — hidden if no data */}
        {showCheckin && (main.checkin_time || main.checkin_instructions) && (
          <Card className="grid-card">
            <CardHeader
              avatar={<Avatar icon="journey-arrive" colorScheme="Accent9" />}
              titleText={checkinLabel}
              subtitleText={isLongTerm ? 'Move-in details' : 'Arrival information'}
            />
            <div className="card-body">
              {main.checkin_time && (
                <FlexBox alignItems="Center" className="checkout-time-row">
                  <Icon name="clock" className="info-icon" />
                  <Label className="info-label">{checkinLabel} at</Label>
                  <Tag design="Set2" colorScheme="5">
                    {main.checkin_time}
                  </Tag>
                </FlexBox>
              )}
              {main.checkin_instructions && (
                <div className="checkout-notes">
                  <Label className="info-label">Instructions</Label>
                  <Text className="info-value pre-wrap">{main.checkin_instructions}</Text>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Checkout / Move-out Card — hidden if no data */}
        {showCheckin && (main.checkout_time || main.checkout) && (
          <Card className="grid-card">
            <CardHeader
              avatar={<Avatar icon="journey-depart" colorScheme="Accent2" />}
              titleText={checkoutLabel}
              subtitleText={isLongTerm ? 'Move-out details' : 'Before you leave'}
            />
            <div className="card-body">
              {main.checkout_time && main.checkout_time !== '' && (
                <FlexBox alignItems="Center" className="checkout-time-row">
                  <Icon name="clock" className="info-icon" />
                  <Label className="info-label">{checkoutLabel} by</Label>
                  <Tag design="Set2" colorScheme="1">
                    {main.checkout_time}
                  </Tag>
                </FlexBox>
              )}
              {main.checkout && (
                <div className="checkout-notes">
                  <Label className="info-label">Notes</Label>
                  <Text className="info-value pre-wrap">{main.checkout}</Text>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Access Codes Card — hidden if no codes */}
        {showAccessCodes && accessCodes && accessCodes.length > 0 && (
          <Card className="grid-card">
            <CardHeader
              avatar={<Avatar icon="key" colorScheme="Accent3" />}
              titleText="Access Codes"
              subtitleText="Doors, gates & lockboxes"
            />
            <div className="card-body-list">
              <List>
                {accessCodes.map((item, index) => (
                  <ListItemStandard
                    key={index}
                    description={item.description}
                    additionalText={item.value}
                    additionalTextState="Information"
                  >
                    {item.label}
                  </ListItemStandard>
                ))}
              </List>
            </div>
          </Card>
        )}

        {/* Local Tips Card — hidden if no tips */}
        {showLocalTips && localTips && localTips.length > 0 && (
          <Card className="grid-card grid-card-full">
            <CardHeader
              avatar={<Avatar icon="map" colorScheme="Accent8" />}
              titleText="Local Tips"
              subtitleText="Our favorite spots nearby"
            />
            <div className="card-body-list">
              <List>
                {localTips.map((item, index) => (
                  <ListItemStandard
                    key={index}
                    description={item.description}
                    additionalText={CATEGORY_MAP[item.category] || ''}
                    additionalTextState="Information"
                  >
                    {item.label}
                  </ListItemStandard>
                ))}
              </List>
            </div>
          </Card>
        )}

        {/* Location Map — shown after Local Tips so pins are contextually relevant */}
        {showMap && location && location.zipCode && (
          <LocationMap
            location={location}
            localTips={localTips}
            showLocalTips={showLocalTips}
          />
        )}

        {/* House Rules & Safety Card — hidden if no data */}
        {showHouseRules && (main.house_rules || main.emergency_contacts) && (
          <Card className="grid-card grid-card-full">
            <CardHeader
              avatar={<Avatar icon="shield" colorScheme="Accent4" />}
              titleText="House Rules & Safety"
              subtitleText="Please review before your stay"
            />
            <div className="card-body">
              {main.house_rules && (
                <div className="checkout-notes">
                  <Label className="info-label">House Rules</Label>
                  <Text className="info-value pre-wrap">{main.house_rules}</Text>
                </div>
              )}
              {main.emergency_contacts && (
                <div className="checkout-notes" style={{ marginTop: '1rem' }}>
                  <Label className="info-label">Emergency Contacts</Label>
                  <Text className="info-value pre-wrap">{main.emergency_contacts}</Text>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Guest Rating & Contact Collection Card */}
        {showGuestRating && (
          <GuestRatingCard locationUUID={new URLSearchParams(window.location.search).get('location')} />
        )}
      </div>
    </div>
  );
}

export default AppShell;
